'use client';

import React, { useState } from 'react';

interface Review {
  id: number;
  guest: string;
  property: string;
  host: string;
  rating: number;
  comment: string;
  date: string;
  status: 'published' | 'flagged' | 'removed';
  flag?: string;
}

const reviews: Review[] = [
  { id: 1, guest: 'Sarah Nakato', property: 'Safari Lodge Murchison', host: 'David Ochieng', rating: 5, comment: 'Absolutely breathtaking experience! Woke up to elephants outside our window.', date: 'Aug 15, 2026', status: 'published' },
  { id: 2, guest: 'Moses Kato', property: 'City Apartment Kampala', host: 'Amina Hassan', rating: 1, comment: 'Terrible experience. Property was nothing like the photos. Host was unresponsive.', date: 'Aug 14, 2026', status: 'flagged', flag: 'Potential fraud claim' },
  { id: 3, guest: 'Peter Mugisha', property: 'Luxury Villa Kololo', host: 'James Ssekandi', rating: 4, comment: 'Beautiful villa, great amenities. Host was very accommodating.', date: 'Aug 12, 2026', status: 'published' },
  { id: 4, guest: 'Grace Achieng', property: 'Lakeside Retreat Entebbe', host: 'Grace Achieng', rating: 2, comment: 'Disappointing. The lake view was blocked by construction. Not worth the price.', date: 'Aug 10, 2026', status: 'flagged', flag: 'Host dispute' },
  { id: 5, guest: 'Ruth Kemigisha', property: 'Mountain Cabin Rwenzori', host: 'Ruth Kemigisha', rating: 5, comment: 'Magical mountain retreat. Perfect for nature lovers.', date: 'Aug 8, 2026', status: 'published' },
];

const statusColors: Record<string, string> = {
  published: 'bg-green-100 text-green-700',
  flagged: 'bg-red-100 text-red-600',
  removed: 'bg-gray-100 text-gray-500',
};

export default function AdminReviews() {
  const [items, setItems] = useState<Review[]>(reviews);
  const [filter, setFilter] = useState('all');

  const handleAction = (id: number, action: 'published' | 'removed') => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, status: action } : r)));
  };

  const filtered = items.filter((r) => filter === 'all' || r.status === filter);
  const flaggedCount = items.filter((r) => r.status === 'flagged').length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Reviews', value: items.length.toString(), color: 'text-gray-900' },
          { label: 'Flagged', value: flaggedCount.toString(), color: 'text-amber-500' },
          { label: 'Avg. Platform Rating', value: '4.7', color: 'text-amber-500' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5">
        {(['all', 'published', 'flagged', 'removed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all flex items-center gap-1.5 ${
              filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
            }`}
          >
            {f}
            {f === 'flagged' && flaggedCount > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === 'flagged' ? 'bg-white/30 text-white' : 'bg-amber-100 text-amber-600'}`}>
                {flaggedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reviews */}
      <div className="space-y-4">
        {filtered.map((review) => (
          <div key={review.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${review.status === 'flagged' ? 'border-amber-200' : 'border-gray-100'}`}>
            <div className="p-5">
              {review.status === 'flagged' && review.flag && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">
                  <span className="text-amber-500">⚠️</span>
                  <span className="text-xs font-semibold text-amber-700">Flagged: {review.flag}</span>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4088C6] to-[#3070a6] flex items-center justify-center text-white text-xs font-bold">
                      {review.guest.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{review.guest}</p>
                      <p className="text-xs text-gray-400">{review.property} · {review.date}</p>
                    </div>
                    <div className="flex items-center gap-0.5 ml-2">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span key={s} className={`text-sm ${s <= review.rating ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{review.comment}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[review.status]}`}>
                    {review.status}
                  </span>
                  {review.status === 'flagged' && (
                    <>
                      <button
                        onClick={() => handleAction(review.id, 'published')}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 transition-colors"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handleAction(review.id, 'removed')}
                        className="text-xs font-semibold text-red-500 hover:text-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </>
                  )}
                  {review.status === 'published' && (
                    <button
                      onClick={() => handleAction(review.id, 'removed')}
                      className="text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
